import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../shared/components/Icon';
import { getNumericCountOperators, getDidOperators } from '../config/userFilterProperties';
import { getDataTypeIcon } from './UserPropertyFilterModule';
import WhoTimeFilterDropdown from './WhoTimeFilterDropdown';

const OperatorDropdown = ({ value, operators, onChange, minWidth = '100px' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const buttonRef = useRef(null);
  const safeOperators = Array.isArray(operators) && operators.length > 0 ? operators : [{ value: 'did', label: 'Did' }];
  const selectedOperator = safeOperators.find((op) => op.value === value) || safeOperators[0];

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const scrollX = window.scrollX || window.pageXOffset;
      setPosition({ top: rect.bottom + scrollY + 4, left: rect.left + scrollX, width: rect.width });
    }
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((o) => !o);
        }}
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 cursor-pointer hover:bg-gray-200 transition-colors"
        style={{ minWidth }}
      >
        {selectedOperator.label}
      </button>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
            <div
              className="fixed z-[9999] bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto"
              style={{
                top: `${position.top}px`,
                left: `${position.left}px`,
                width: `${Math.max(position.width || 120, 120)}px`,
              }}
            >
              <div className="py-1">
                {safeOperators.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    onClick={() => {
                      onChange(op.value);
                      setIsOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center ${value === op.value ? 'bg-gray-200 text-[#0f172a]' : ''}`}
                  >
                    <span>{op.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
};

/**
 * WHO row for groups: event count per member in time window (events only, no transactions).
 */
const GroupWhoFilterChip = ({ filter, isFirst, onEdit, onRemove, onUpdate }) => {
  const connector = filter.connector || (isFirst ? 'WHO' : 'AND');
  const type = filter.propertyType || 'number';
  const DataTypeIcon = getDataTypeIcon(type);
  const operators = getNumericCountOperators();
  const didOperators = getDidOperators();
  const defaultOp = operators[0]?.value || 'equal';
  const currentOperator = filter.operator || defaultOp;
  const currentDidOperator = filter.didOperator || 'did';

  const field = filter.field || filter.property;
  const eventName =
    typeof field === 'string' && field.startsWith('event_count:')
      ? field.slice('event_count:'.length)
      : filter.propertyLabel || '';

  const handleChipUpdate = (updates) => {
    onUpdate(updates);
  };

  const renderValueInput = () => {
    if (currentOperator === 'is_numeric' || currentOperator === 'is_not_numeric') return null;
    if (currentOperator === 'between' || currentOperator === 'not_between') {
      return (
        <div className="flex items-center gap-1">
          <input
            type="number"
            value={filter.value ?? ''}
            onChange={(e) => handleChipUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
            placeholder="From..."
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
            onClick={(e) => e.stopPropagation()}
          />
          <span className="text-xs text-[#475569]">to</span>
          <input
            type="number"
            value={filter.value2 ?? ''}
            onChange={(e) => handleChipUpdate({ value2: e.target.value === '' ? null : parseFloat(e.target.value) })}
            placeholder="To..."
            className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-20"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      );
    }
    return (
      <input
        type="number"
        value={filter.value ?? ''}
        onChange={(e) => handleChipUpdate({ value: e.target.value === '' ? null : parseFloat(e.target.value) })}
        placeholder="Count"
        className="px-2.5 py-1.5 text-xs bg-gray-100 rounded-lg text-center text-[#0f172a] focus:outline-none focus:ring-0 hover:bg-gray-200 transition-colors w-28"
        onClick={(e) => e.stopPropagation()}
      />
    );
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {isFirst ? (
        <span className="text-xs font-medium text-[#475569] px-2">WHO</span>
      ) : (
        <OperatorDropdown
          value={connector}
          operators={[
            { value: 'AND', label: 'AND' },
            { value: 'OR', label: 'OR' },
          ]}
          onChange={(c) => handleChipUpdate({ connector: c })}
          minWidth="60px"
        />
      )}
      <OperatorDropdown
        value={currentDidOperator}
        operators={didOperators}
        onChange={(d) => handleChipUpdate({ didOperator: d })}
        minWidth="90px"
      />
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onEdit();
          }
        }}
        className="inline-flex items-center gap-2 px-2.5 py-1.5 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
      >
        <DataTypeIcon className="h-4 w-4 text-[#475569] flex-shrink-0" />
        <span
          className="text-[10px] font-semibold uppercase w-5 h-5 inline-flex items-center justify-center rounded bg-gray-200 text-[#475569] flex-shrink-0"
          title="Event"
        >
          E
        </span>
        <span className="text-sm text-[#0f172a] font-medium">{eventName || filter.propertyLabel}</span>
      </div>
      <OperatorDropdown
        value={currentOperator}
        operators={operators}
        onChange={(op) => handleChipUpdate({ operator: op, value2: null })}
        minWidth="100px"
      />
      {renderValueInput()}
      <WhoTimeFilterDropdown value={filter.eventTimeFilter} onChange={(eventTimeFilter) => handleChipUpdate({ eventTimeFilter })} />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-1.5 text-[#475569] hover:text-[#475569] transition-colors"
        title="Delete filter"
      >
        <Icon name="delete" size={16} />
      </button>
    </div>
  );
};

export default GroupWhoFilterChip;
